import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { TeamViewToggle } from "./TeamViewToggle";
import { useAuth, roleLabel } from "../lib/auth";
import { useFollowUpReminders } from "../lib/reminders";
import { useIdleLogout } from "../lib/useIdleLogout";
import BroadcastPopup from "./BroadcastPopup";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, PhoneCall, LogOut,
  Sun, Moon, Menu, X, Settings,
  Building2, MapPinned, ClipboardList, Inbox,
  IndianRupee, CalendarCheck, FileText, BellRing, Trophy, Flame, Snowflake, Zap, Handshake, XCircle,
  Megaphone, UserCog, CalendarDays, History,
  UserPlus, KeyRound, LogIn as LogInIcon, ClipboardCheck, BarChart3, ShieldCheck, Ban,
  Share2, Send, Ghost, Star, SlidersHorizontal, Hash,
} from "lucide-react";
import { api } from "../lib/api";
import { Avatar, AvatarFallback } from "./ui/avatar";

// Accent palette used for the tinted icon squares. Cycles per nav item.
// Light theme: pastel tint · Dark theme: electric neon tint.
const NAV_ACCENTS = [
  { light: "bg-[#F3EEFF] text-[#7C3AED]", dark: "dark:bg-[#7C3AED]/15 dark:text-[#C7B8FF] dark:ring-1 dark:ring-inset dark:ring-[#7C3AED]/25" }, // purple
  { light: "bg-[#EAF2FF] text-[#4F8CFF]", dark: "dark:bg-[#4F8CFF]/15 dark:text-[#93B8FF] dark:ring-1 dark:ring-inset dark:ring-[#4F8CFF]/25" }, // blue
  { light: "bg-[#E7F8F1] text-[#10B981]", dark: "dark:bg-[#10B981]/15 dark:text-[#6EE7B7] dark:ring-1 dark:ring-inset dark:ring-[#10B981]/25" }, // emerald
  { light: "bg-[#FFF3E4] text-[#F59E0B]", dark: "dark:bg-[#F59E0B]/15 dark:text-[#FCD34D] dark:ring-1 dark:ring-inset dark:ring-[#F59E0B]/25" }, // orange
  { light: "bg-[#FDEAF3] text-[#EC4899]", dark: "dark:bg-[#EC4899]/15 dark:text-[#F9A8D4] dark:ring-1 dark:ring-inset dark:ring-[#EC4899]/25" }, // pink
];
const getAccent = (idx) => NAV_ACCENTS[idx % NAV_ACCENTS.length];

// CRM Access: per-user permission toggles win; fall back to role defaults.
export const canSee = (u, item) => {
  if (!u) return false;
  const perms = u.permissions;
  if (item.perm && perms && item.perm in perms) return !!perms[item.perm];
  return !item.roles || item.roles.includes(u.role);
};

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, perm: "dashboard" },
];

// Real-estate specific sections
const REAL_ESTATE_NAV = [
  { to: "/projects",                          label: "Projects",         icon: Building2, live: true, perm: "projects" },
  { to: "/site-visits",                       label: "Site Visits",      icon: MapPinned, live: true, perm: "site_visits" },
];
const ME_NAV = [
  { to: "/requests",                          label: "Request for Data", icon: Inbox, live: true, badgeKey: "requests", perm: "requests" },
  { to: "/attendance",                        label: "My Attendance",    icon: CalendarCheck, live: true, perm: "attendance" },
  { to: "/day-end-report",                    label: "Day End Report",   icon: BarChart3, live: true, perm: "day_end_report" },
];

const ADMIN_NAV = [
  // Tele-sales-facing lead filters (Status section) — shown to everyone, but for tele_sales
  // this section is renamed to "Status" and lands directly under the Dashboard.
  { to: "/leads?status=New",                                                                                     label: "New Leads",     icon: UserPlus,       roles: ["super_admin", "admin", "team_leader", "tele_sales"], badgeKey: "newLeads", perm: "leads" },
  { to: "/lead-pool",                                                                                            label: "Lead Pool",     icon: ClipboardList,  roles: ["super_admin", "admin", "team_leader", "tele_sales"], badgeKey: "leadPool", perm: "lead_pool" },
  { to: "/leads?status_in=Lost,Not%20Interested,Invalid%20Number,Duplicate&title=Lost+Leads",                     label: "Lost Leads",    icon: Ban,            roles: ["super_admin", "admin", "team_leader", "tele_sales"], badgeKey: "lostLeads", perm: "leads" },
  { to: "/leads?status_in=Follow%20Up,Call%20Back,Requirement",                                                   label: "Follow-ups",    icon: PhoneCall,      roles: ["super_admin", "admin", "team_leader", "tele_sales"], perm: "leads" },
  { to: "/leads/starred",                                                                                        label: "Starred Leads", icon: Star,           roles: ["super_admin", "admin", "team_leader", "tele_sales", "hr", "accounts"], badgeKey: "starredLeads", perm: "starred" },
  { to: "/team-performance",      label: "Team Performance", icon: UserCog,   roles: ["super_admin", "admin", "team_leader"], perm: "team_performance" },
  { to: "/team-attendance",       label: "Team Attendance",  icon: CalendarDays, roles: ["super_admin", "admin", "hr"], perm: "team_attendance" },
  { to: "/leads-management",      label: "Approval Inbox",   icon: Inbox, roles: ["super_admin"], badgeKey: "leadsMgmt", perm: "approval_inbox" },
  { to: "/dummy-leads",           label: "Dummy Section",    icon: Ghost, roles: ["super_admin"], badgeKey: "dummyLeads", perm: "dummy" },
  { to: "/signup-requests",       label: "Signup Requests",  icon: UserPlus, roles: ["super_admin"], badgeKey: "signups", perm: "signup_requests" },
  { to: "/login-sessions",        label: "Login Sessions",   icon: LogInIcon, roles: ["super_admin", "admin"], perm: "login_sessions" },
  { to: "/status-updates",        label: "Status Updates",   icon: ClipboardList, roles: ["super_admin"], perm: "status_updates" },
  { to: "/access",                label: "Access Control",   icon: ShieldCheck, roles: ["super_admin"], perm: "access_control" },
  { to: "/crm-access",            label: "CRM Access",       icon: SlidersHorizontal, roles: ["super_admin"], perm: "crm_access" },
  { to: "/source-codes",          label: "Source Codes",     icon: Hash, roles: ["super_admin"], perm: "crm_access" },
  { to: "/activity",              label: "Activity Log",     icon: History,   roles: ["super_admin", "admin", "team_leader", "hr"], perm: "activity_log" },
  { to: "/password-resets",       label: "Password Resets",  icon: KeyRound,  roles: ["super_admin", "admin"], perm: "password_resets" },
  { to: "/settings/role-targets", label: "Role Targets",  icon: Settings,  roles: ["super_admin"], perm: "role_targets" },
  { to: "/broadcasts",            label: "Announcements", icon: Megaphone, roles: ["super_admin", "admin"], perm: "broadcasts" },
  { to: "/shared-with-me",        label: "Shared with Me",  icon: Share2,   roles: ["super_admin", "admin", "team_leader", "tele_sales"], perm: "shares", badgeKey: "sharedWithMe" },
  { to: "/shared-by-me",          label: "Shared by Me",    icon: Send,     roles: ["super_admin", "admin", "team_leader", "tele_sales"], perm: "shares", badgeKey: "sharedByMe" },
];

export default function Layout() {
  const { user, logout, impersonateUser, setImpersonateUser } = useAuth();
  // Effective identity for sidebar/routing decisions. Writes still use the
  // real `user` (backend auth = Bearer token belonging to SA/Admin).
  const effectiveUser = impersonateUser || user;
  const location = useLocation();
  const navigate = useNavigate();
  useFollowUpReminders(!!user);
  useIdleLogout({
    enabled: !!user,
    logoutEnabled: !!user?.auto_logout_idle && user?.role !== "super_admin",
    onIdle: async () => {
      toast.warning("Signed out after 15 minutes of inactivity", { duration: 6000 });
      try { window.__gbManualLogout = true; await api.post("/auth/logout"); } catch { /* ignore */ }
      logout();
      navigate("/login");
    },
  });
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("gb_theme");
    if (stored) return stored === "dark";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [badges, setBadges] = useState({ requests: 0, leadsMgmt: 0, signups: 0, dummyLeads: 0, starredLeads: 0, lostLeads: 0, leadPool: 0, newLeads: 0 });

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  // Fetch admin badges (pending data requests + pending transfer approvals) for super/admin only
  useEffect(() => {
    if (!user || !["super_admin", "admin"].includes(user.role)) return;
    const load = async () => {
      try {
        const [r, l, s, d] = await Promise.all([
          api.get("/data-requests/pending-count").catch(() => ({ data: { count: 0 } })),
          api.get("/transfer-requests/pending-count").catch(() => ({ data: { count: 0 } })),
          api.get("/signup-requests/pending-count").catch(() => ({ data: { count: 0 } })),
          ["super_admin", "admin"].includes(user.role)
            ? api.get("/leads/dummy/list").catch(() => ({ data: { total: 0 } }))
            : Promise.resolve({ data: { total: 0 } }),
        ]);
        setBadges((b) => ({
          ...b,
          requests: r.data.count || 0,
          leadsMgmt: l.data.count || 0,
          signups: s.data.count || 0,
          dummyLeads: d.data.total || 0,
        }));
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60 * 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  // Per-user lead badges — visible to EVERY role. Each badge fetches its own
  // count from an independent endpoint (never a shared or cached total) so
  // that Starred / Lost / Lead Pool / New Leads move in lock-step with the
  // actual data. All badges refresh instantly on any `gb-leads-changed` or
  // `gb-star-changed` event (fired from mutation sites) — no dependence on
  // the 60-second polling clock.
  useEffect(() => {
    if (!user) return;
    const loadStarred = async () => {
      try {
        const r = await api.get("/leads/starred/count");
        setBadges((b) => ({ ...b, starredLeads: r.data?.count || 0 }));
      } catch { /* ignore */ }
    };
    const loadLost = async () => {
      try {
        const r = await api.get("/leads?status_in=Lost,Not%20Interested,Invalid%20Number,Duplicate&limit=1");
        setBadges((b) => ({ ...b, lostLeads: r.data?.total || 0 }));
      } catch { /* ignore */ }
    };
    const loadNew = async () => {
      try {
        const r = await api.get("/leads?status=New&limit=1");
        setBadges((b) => ({ ...b, newLeads: r.data?.total || 0 }));
      } catch { /* ignore */ }
    };
    const loadPool = async () => {
      try {
        const r = await api.get("/lead-pool/counts");
        setBadges((b) => ({ ...b, leadPool: r.data?.total || 0 }));
      } catch { /* ignore */ }
    };
    const loadShares = async () => {
      try {
        const r = await api.get("/lead-shares/counts");
        setBadges((b) => ({ ...b, sharedWithMe: r.data?.received || 0, sharedByMe: r.data?.sent || 0 }));
      } catch { /* ignore */ }
    };
    const loadAll = () => { loadStarred(); loadLost(); loadNew(); loadPool(); loadShares(); };
    loadAll();
    window.addEventListener("gb-shares-changed", loadShares);
    const onVisible = () => { if (!document.hidden) loadAll(); };
    document.addEventListener("visibilitychange", onVisible);
    const onStar = () => loadStarred();
    const onLeads = () => { loadLost(); loadNew(); loadPool(); loadStarred(); loadShares(); };
    window.addEventListener("gb-star-changed", onStar);
    window.addEventListener("gb-leads-changed", onLeads);
    const iv = setInterval(() => { if (!document.hidden) loadAll(); }, 60 * 1000);
    return () => {
      window.removeEventListener("gb-star-changed", onStar);
      window.removeEventListener("gb-leads-changed", onLeads);
      window.removeEventListener("gb-shares-changed", loadShares);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("gb_theme", dark ? "dark" : "light");
  }, [dark]);

  // Close drawer on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const initials = (user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const closeDrawer = () => setDrawerOpen(false);
  const handleLogout = async () => {
    closeDrawer();
    try { window.__gbManualLogout = true; await api.post("/auth/logout"); } catch { /* still sign out */ }
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Persistent hamburger — always visible top-left */}
      <button
        onClick={() => setDrawerOpen(true)}
        data-testid="drawer-open"
        aria-label="Open menu"
        className="fixed top-4 left-4 z-30 h-11 w-11 rounded-md bg-card border border-border shadow-sm flex items-center justify-center text-foreground hover:bg-secondary transition-colors"
      >
        <Menu className="h-5 w-5" />
      </button>

      {!location.pathname.startsWith("/team-performance") && <TeamViewToggle className="fixed top-4 right-4 z-30" />}

      {/* Backdrop */}
      <div
        onClick={closeDrawer}
        data-testid="drawer-backdrop"
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-200
          ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />

      {/* Slide-out sidebar drawer */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-50 h-screen w-72 flex flex-col transition-transform duration-250 ease-out
          border-r
          bg-[linear-gradient(180deg,#FFFFFF_0%,#F8FAFC_55%,#F5F3FF_100%)] border-[#E8ECF4]
          shadow-[8px_0_28px_-14px_rgba(124,58,237,0.14)]
          text-slate-800
          dark:bg-[linear-gradient(180deg,#0B1220_0%,#111827_55%,#1F2937_100%)] dark:border-[#1E293B]
          dark:shadow-[8px_0_28px_-14px_rgba(0,0,0,0.6)]
          dark:text-slate-200
          ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center justify-between h-20 px-4 border-b border-[#E8ECF4] dark:border-[#1E293B] shrink-0
                        bg-gradient-to-b from-white/80 via-white/40 to-transparent
                        dark:from-[#0B1220] dark:via-[#0B1220]/70 dark:to-transparent
                        backdrop-blur-md">
          <Link to="/" onClick={closeDrawer} className="flex items-center gap-3 min-w-0 group" data-testid="sidebar-brand">
            <div className="relative shrink-0">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#e6c584] via-[#B78E4C] to-[#8f6c30] flex items-center justify-center font-display font-black text-white text-xl shadow-[0_6px_18px_-4px_rgba(183,142,76,0.45)] ring-1 ring-white/60 dark:ring-white/10 group-hover:scale-105 transition-transform">
                G
              </div>
              <span className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 border-2 border-white dark:border-[#0B1220] shadow-sm" title="Live"></span>
            </div>
            <div className="leading-none min-w-0">
              <div className="font-display font-black text-[15px] tracking-tight truncate
                              text-[#0F172A]
                              dark:bg-gradient-to-br dark:from-[#EAE5FF] dark:via-[#C7B8FF] dark:to-[#93B8FF] dark:bg-clip-text dark:text-transparent">
                Get Best Property
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1.5">
                <span className="h-px w-4 bg-gradient-to-r from-transparent to-[#B78E4C]/70" />
                <span className="text-[10px] font-mono font-bold tracking-[0.35em] text-[#B78E4C] dark:text-[#D4A017]">CRM</span>
                <span className="h-px w-4 bg-gradient-to-l from-transparent to-[#B78E4C]/70" />
              </div>
            </div>
          </Link>
          <button
            className="h-8 w-8 rounded-md flex items-center justify-center transition-colors
                       text-slate-500 hover:text-slate-900 hover:bg-slate-100
                       dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/5"
            onClick={closeDrawer}
            data-testid="drawer-close"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {NAV.filter((it) => canSee(effectiveUser, it)).map((item, idx) => {
            const a = getAccent(idx);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={closeDrawer}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all
                   ${isActive
                      ? "text-[#5B21B6] bg-[linear-gradient(90deg,#F3EEFF_0%,#FAF7FF_100%)] shadow-[0_4px_14px_-6px_rgba(124,58,237,0.35)] " +
                        "dark:text-[#DDD6FE] dark:bg-[linear-gradient(90deg,rgba(124,58,237,0.18)_0%,rgba(79,140,255,0.12)_100%)] dark:shadow-[0_0_0_1px_rgba(124,58,237,0.35),0_8px_24px_-8px_rgba(124,58,237,0.55)]"
                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"}`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b from-[#7C3AED] via-[#8B5CF6] to-[#EC4899] shadow-[0_0_10px_rgba(124,58,237,0.55)] dark:from-[#22D3EE] dark:via-[#4F8CFF] dark:to-[#A78BFA] dark:shadow-[0_0_12px_rgba(34,211,238,0.7)]" aria-hidden="true" />
                    )}
                    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${a.light} ${a.dark}`}>
                      <item.icon className="h-4 w-4" />
                    </span>
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}

          <NavSection label="Property" />
          {REAL_ESTATE_NAV
            .filter((it) => canSee(effectiveUser, it))
            .map((item, idx) => (
              <NavItemComingSoon key={item.label} item={item} onNav={closeDrawer} accent={getAccent(idx + 1)} />
          ))}

          {ADMIN_NAV.filter((it) => canSee(effectiveUser, it)).length > 0 && (
            <NavSection label={effectiveUser?.role === "tele_sales" ? "Status" : "Admin"} />
          )}
          {ADMIN_NAV.filter((it) => canSee(effectiveUser, it)).map((item, idx) => {
            const a = getAccent(idx);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={closeDrawer}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all
                   ${isActive
                      ? "text-[#5B21B6] bg-[linear-gradient(90deg,#F3EEFF_0%,#FAF7FF_100%)] shadow-[0_4px_14px_-6px_rgba(124,58,237,0.35)] " +
                        "dark:text-[#DDD6FE] dark:bg-[linear-gradient(90deg,rgba(124,58,237,0.18)_0%,rgba(79,140,255,0.12)_100%)] dark:shadow-[0_0_0_1px_rgba(124,58,237,0.35),0_8px_24px_-8px_rgba(124,58,237,0.55)]"
                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"}`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b from-[#7C3AED] via-[#8B5CF6] to-[#EC4899] shadow-[0_0_10px_rgba(124,58,237,0.55)] dark:from-[#22D3EE] dark:via-[#4F8CFF] dark:to-[#A78BFA] dark:shadow-[0_0_12px_rgba(34,211,238,0.7)]" aria-hidden="true" />
                    )}
                    <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${a.light} ${a.dark}`}>
                      <item.icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badgeKey && badges[item.badgeKey] > 0 && (
                      <span data-testid={`nav-badge-${item.badgeKey}`} className="ml-auto inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-[10px] font-bold rounded-full bg-gradient-to-br from-[#EF4444] to-[#EC4899] text-white shadow-[0_2px_8px_-2px_rgba(236,72,153,0.55)] dark:from-[#F43F5E] dark:to-[#EC4899] dark:shadow-[0_0_10px_rgba(236,72,153,0.65)]">
                        {badges[item.badgeKey]}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}

          <NavSection label="Me" />
          {ME_NAV.filter((it) => canSee(effectiveUser, it)).map((item, idx) => (
            <NavItemComingSoon
              key={item.label}
              item={item}
              onNav={closeDrawer}
              badge={item.badgeKey ? badges[item.badgeKey] : 0}
              accent={getAccent(idx + 2)}
            />
          ))}
        </nav>

        <div className="border-t border-[#E8ECF4] dark:border-[#1E293B] shrink-0
                        bg-[linear-gradient(180deg,transparent_0%,rgba(245,243,255,0.6)_100%)]
                        dark:bg-[linear-gradient(180deg,transparent_0%,rgba(15,23,42,0.4)_100%)]">
          {/* User card + theme toggle */}
          <div className="p-3">
            <div
              data-testid="sidebar-user"
              className="relative overflow-hidden flex items-center gap-2.5 rounded-xl px-2.5 py-2.5
                         border border-[#E4DAF5] bg-[linear-gradient(120deg,#FAF5FF_0%,#FDF2F8_60%,#FFFFFF_100%)]
                         shadow-[0_4px_18px_-8px_rgba(124,58,237,0.28)]
                         dark:border-[#1E2A4D] dark:bg-[linear-gradient(120deg,rgba(30,42,80,0.6)_0%,rgba(30,64,175,0.35)_60%,rgba(15,23,42,0.6)_100%)]
                         dark:shadow-[0_0_0_1px_rgba(79,140,255,0.15),0_8px_22px_-10px_rgba(34,211,238,0.35)]"
            >
              <div className="pointer-events-none absolute -top-6 -right-4 h-16 w-16 rounded-full bg-[#7C3AED]/15 dark:bg-[#22D3EE]/20 blur-2xl" />
              <Avatar className="h-9 w-9 shrink-0 ring-2 ring-white/70 dark:ring-white/10">
                <AvatarFallback className="bg-gradient-to-br from-[#7C3AED] to-[#EC4899] dark:from-[#22D3EE] dark:to-[#4F8CFF] text-white text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="relative leading-tight min-w-0">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-50 truncate">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-wider truncate text-[#7C3AED] dark:text-[#93B8FF] font-medium">
                  {roleLabel(user?.role)}
                </div>
              </div>
            </div>
          </div>

          <div className="px-3">
            <button
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle"
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors
                         text-slate-700 hover:bg-slate-100
                         dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#F3EEFF] text-[#7C3AED] dark:bg-[#22D3EE]/15 dark:text-[#67E8F9]">
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </span>
              <span>{dark ? "Light mode" : "Dark mode"}</span>
            </button>
          </div>

          {/* Logout — appears just above the required 3cm bottom gap */}
          <div className="px-3 pt-2">
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors
                         text-slate-700 hover:bg-rose-50 hover:text-rose-600
                         dark:text-slate-300 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
            >
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#FDEAF3] text-[#EC4899] dark:bg-[#F43F5E]/15 dark:text-[#FCA5A5]">
                <LogOut className="h-4 w-4" />
              </span>
              <span>Logout</span>
            </button>
          </div>

          {/* 3cm gap below logout as requested */}
          <div className="h-[3cm]" aria-hidden="true" data-testid="sidebar-bottom-gap" />
        </div>
      </aside>

      {/* Main — centered content, 2cm top gap on every page */}
      <main className="min-h-screen px-3 sm:px-4 lg:pl-20 lg:pr-8 pb-10 pt-[2cm] w-full max-w-full">
        <div className="max-w-7xl mx-auto w-full min-w-0">
          {impersonateUser && (
            <div
              data-testid="impersonation-global-banner"
              className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-[#B78E4C]/50 bg-[#B78E4C]/10 px-4 py-2.5 text-sm"
            >
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4 text-[#B78E4C]" />
                <span>
                  Viewing as <span className="font-semibold text-[#B78E4C]">{impersonateUser.name}</span>
                  {" "}
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">
                    ({roleLabel(impersonateUser.role)})
                  </span>
                  <span className="text-muted-foreground ml-2">· edits still recorded under your account</span>
                </span>
              </div>
              <button
                onClick={() => { setImpersonateUser(null); navigate("/"); }}
                data-testid="impersonation-global-exit"
                className="text-xs font-semibold text-[#B78E4C] hover:underline"
              >
                Exit view
              </button>
            </div>
          )}
          <React.Suspense fallback={<div className="p-8 text-sm text-muted-foreground" data-testid="page-loading">Loading…</div>}>
            <Outlet />
          </React.Suspense>
        </div>
      </main>

      <BroadcastPopup user={user} />
    </div>
  );
}

function NavSection({ label }) {
  return (
    <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-[0.2em] font-semibold
                    text-[#7C3AED]/70
                    dark:text-[#93B8FF]/80">
      {label}
    </div>
  );
}

function NavItemComingSoon({ item, onNav, badge = 0, accent }) {
  const a = accent || { light: "bg-[#F3EEFF] text-[#7C3AED]", dark: "dark:bg-[#7C3AED]/15 dark:text-[#C7B8FF]" };
  return (
    <NavLink
      to={item.to}
      onClick={onNav}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all
         ${isActive
            ? "text-[#5B21B6] bg-[linear-gradient(90deg,#F3EEFF_0%,#FAF7FF_100%)] shadow-[0_4px_14px_-6px_rgba(124,58,237,0.35)] " +
              "dark:text-[#DDD6FE] dark:bg-[linear-gradient(90deg,rgba(124,58,237,0.18)_0%,rgba(79,140,255,0.12)_100%)] dark:shadow-[0_0_0_1px_rgba(124,58,237,0.35),0_8px_24px_-8px_rgba(124,58,237,0.55)]"
            : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-white"}`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-gradient-to-b from-[#7C3AED] via-[#8B5CF6] to-[#EC4899] shadow-[0_0_10px_rgba(124,58,237,0.55)] dark:from-[#22D3EE] dark:via-[#4F8CFF] dark:to-[#A78BFA] dark:shadow-[0_0_12px_rgba(34,211,238,0.7)]" aria-hidden="true" />
          )}
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${a.light} ${a.dark}`}>
            <item.icon className="h-4 w-4" />
          </span>
          <span className="flex-1 truncate">{item.label}</span>
          {badge > 0 && (
            <span
              data-testid={`nav-badge-${item.badgeKey}`}
              className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#EC4899] text-white text-[10px] font-bold shadow-[0_2px_8px_-2px_rgba(236,72,153,0.5)] dark:from-[#22D3EE] dark:to-[#A78BFA] dark:shadow-[0_0_10px_rgba(34,211,238,0.6)]"
            >
              {badge}
            </span>
          )}
          {!item.live && badge === 0 && (
            <span className="text-[9px] uppercase tracking-widest text-[#B78E4C]/70 dark:text-[#D4A017]/80 opacity-0 group-hover:opacity-100 transition-opacity">
              soon
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
